import styled from '@emotion/styled'
import { Drawer as MuiDrawer, ListItemButton, useTheme } from '@mui/material'
import React from 'react'
import { NavLink } from 'react-router-dom'

import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'
import { SidebarItemsType } from '@/types/sidebar'
import LogoBlack from '@/vendor/logo-black.svg'
import LogoWhite from '@/vendor/logo-white.svg'
import MiniLogoWhite from '@/vendor/mini-logo.svg'
import MiniLogoBlack from '@/vendor/mini-logo-black.svg'

import Footer from './SidebarFooter'
import SidebarNav from './SidebarNav'

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
    scrollbar-width: none;
    overflow-x: hidden;
  }
`

const Brand = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== 'collapsed',
})<{
  component?: React.ReactNode
  to?: string
  collapsed?: boolean
}>`
  font-size: ${props => props.theme.typography.h5.fontSize};
  font-weight: ${props => props.theme.typography.fontWeightMedium};
  color: ${props => props.theme.sidebar.header.color};
  background-color: ${props => props.theme.sidebar.header.background};
  font-family: ${props => props.theme.typography.fontFamily};
  min-height: 56px;
  max-height: 80px;
  padding-left: ${props =>
    props.collapsed ? props.theme.spacing(2) : props.theme.spacing(6)};
  padding-right: ${props =>
    props.collapsed ? props.theme.spacing(2) : props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.12);

  ${props => props.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${props => props.theme.sidebar.header.background};
  }
`

const Image = styled.img<{ collapsed?: boolean }>`
  max-width: 100%;
  max-height: 64px;
  object-fit: contain;
  ${props => (props.collapsed ? 'width: 40px; height: 40px;' : '')}
`

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number
    }
  }
  variant?: 'permanent' | 'persistent' | 'temporary'
  open?: boolean
  onClose?: () => void
  items: {
    title: string
    pages: SidebarItemsType[]
  }[]
  showFooter?: boolean
  onCollapse?: () => void
  collapsed?: boolean
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  showFooter = true,
  onCollapse,
  collapsed = false,
  ...rest
}) => {
  const { user } = useLoggedInUser('cache-first')
  const theme = useTheme()
  const { t } = useT()
  const isDarkMode = theme.palette.mode === 'dark'

  const fullLogo = isDarkMode ? LogoWhite : LogoBlack
  const miniLogo = isDarkMode ? MiniLogoWhite : MiniLogoBlack

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={NavLink as any} to="/" collapsed={collapsed}>
        {/* @ts-expect-error - tenant property not in UserFragment for promo package */}
        {user?.tenant?.logoImage?.url ? (
          <Image
            alt={t('promo.sidebar.logo.company')}
            src={
              // @ts-expect-error - tenant property not in UserFragment for promo package
              collapsed ? miniLogo : user.tenant.logoImage.url
            }
            collapsed={collapsed}
          />
        ) : (
          <Image
            alt={t('promo.sidebar.logo.default')}
            src={collapsed ? miniLogo : fullLogo}
            collapsed={collapsed}
          />
        )}
      </Brand>
      <SidebarNav items={items} collapsed={collapsed} />
      {!!showFooter && <Footer onCollapse={onCollapse} collapsed={collapsed} />}
    </Drawer>
  )
}

export default Sidebar
